import { Trans } from "@lingui/react";
import { Button } from "@warp-ds/elements/react/button";
import DOMPurify from "isomorphic-dompurify";
import { useEffect, useRef, useState } from "react";
import { useOrganisation } from "src/context/DealerDataProviderContext.tsx";
import { trackShowFullDescription } from "src/utils/pulseHelper.ts";
import { Icon } from "./Icon.tsx";

export const Description = ({ className = "" }: { className?: string }) => {
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [canExpand, setCanExpand] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { description } = useOrganisation();

  const toggleExpanded = () => {
    setExpanded((prev) => {
      if (!prev) {
        trackShowFullDescription();
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (descriptionRef.current) {
      const { scrollHeight } = descriptionRef.current;

      setCanExpand(scrollHeight > 110);
    }
  }, []);

  if (!description) {
    return null;
  }

  const sanitizedDescription = DOMPurify.sanitize(description);

  return (
    <div className={`${className}`}>
      <div
        ref={descriptionRef}
        aria-describedby={canExpand ? "dealer-description" : undefined}
        className={`relative whitespace-pre-line ${expanded || !canExpand ? "max-h-none" : "max-h-[110px] overflow-hidden"}`}
      >
        {!expanded && canExpand && (
          <div
            className="absolute top-0 h-full w-full pointer-events-none"
            style={{ background: "linear-gradient(to top, var(--w-s-color-background), transparent)" }}
          />
        )}
        <div>{sanitizedDescription}</div>
      </div>
      {canExpand && (
        <div className={`relative s-bg`}>
          <Button onClick={toggleExpanded} variant="quiet" aria-expanded={expanded} aria-controls="dealer-description">
            {expanded ? (
              <span className="flex items-center gap-8">
                <Icon name="Minus" />
                <Trans id="dealerBrandingPage.description.showLess" message="Show less" />
              </span>
            ) : (
              <span className="flex items-center gap-8">
                <Icon name="Plus" />
                <Trans id="dealerBrandingPage.description.showDescription" message="Show entire description" />
              </span>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
